/**
 * A named set of answers -- a "Testperson" -- and the reading of the one field
 * list that both halves of the product need to build one.
 *
 * WHY THIS FILE IS HERE AND NOT IN editor-v2. Two panels compose the same
 * imagined reader: the editor's "Vorschau mit Antworten"
 * (editor-v2/components/canvas/AnswerPreviewPanel.vue, Vue 3) and the project
 * settings, where a persona is written down once and reused
 * (settings/projects/ProjectSettingsPersonas.vue, Vue 2). If they read the
 * field list differently, or coerce a typed answer differently, a persona
 * would fill the preview with something other than what its author saw when
 * they built it. So the reading and the coercion live here, in plain
 * JavaScript, and BOTH sides import them -- the same reason conditionFields.js
 * and conditionSentence.js next door are here rather than in the editor
 * (Jest cannot resolve `.ts` at all).
 *
 * The lower half of editor-v2/utils/answerPreviewModel.ts -- everything that
 * touches the canvas DOM or the reader's evaluator -- stays where it is: it is
 * the editor's alone, and the settings have no canvas.
 *
 * NOTHING HERE READS A WINDOW GLOBAL. The field list arrives as an argument,
 * exactly as it does in conditionFields.js: the editor has it on `window`, the
 * settings load it over the network, and a module that knew about either one
 * would only work on one side.
 *
 * Type declarations for TypeScript callers live in answerPersonaModel.d.ts --
 * runtime changes here MUST be mirrored there.
 */

// ---------------------------------------------------------------------------
// Reading the ONE field list -- there is no second one here
//
// Everything below is derived from what collectConditionFields() returned:
// the same list every condition in this product is built from. Nothing here
// reads `__formsData`, `__ciConfig` or a form document directly. A fourth
// field list would be the one that goes stale, and a persona has to agree with
// the condition editor field for field, or it would answer questions nobody
// can ask.
// ---------------------------------------------------------------------------

/** The form id and the field id inside `form.<formId>.<fieldId>`, or null. */
export function splitAnswerPath(path) {
  const parts = String(path || '').split('.');
  if (parts.length !== 3 || parts[0] !== 'form') return null;
  if (!parts[1] || !parts[2]) return null;
  return { formId: parts[1], fieldId: parts[2] };
}

/** The groups holding form answers, in the order the field list has them.
 *  Fields marked `derived` (calculated variables, "Formular abgeschickt") are
 *  left out: a persona and the answer preview stand for a reader, and a
 *  reader answers questions -- the form calculates the rest. */
export function previewAnswerGroups(groups) {
  return (groups || [])
    .map((group) => ({
      label: group.label,
      fields: group.fields.filter(
        (field) => field.kind === 'form' && !field.derived && splitAnswerPath(field.path),
      ),
    }))
    .filter((group) => group.fields.length > 0);
}

/** The path the field list uses for "which set does the reader belong to". */
const SET_ITSELF_PATH = 'personalized_variable_set_id';

function fieldByPath(groups, kind, path) {
  const list = groups || [];
  for (let i = 0; i < list.length; i += 1) {
    const hit = list[i].fields.find((f) => f.kind === kind && f.path === path);
    if (hit) return hit;
  }
  return null;
}

/**
 * The personalization sets on offer, taken from the value table the field list
 * already carries for "Das Personalisierungs-Set selbst" (conditionFields.js)
 * -- which is where the set ids and names live, so no second source is needed.
 */
export function previewPersonalizationSets(groups) {
  const field = fieldByPath(groups, 'personalization', SET_ITSELF_PATH);
  if (!field || !field.options) return [];
  return field.options.map((option) => ({ id: option.value, label: option.label }));
}

/** The variables one set carries: its fields are stored as `<setId>.<entry>`. */
export function previewPersonalizationFields(groups, setId) {
  if (!setId) return [];
  const prefix = `${setId}.`;
  const out = [];
  (groups || []).forEach((group) => {
    group.fields.forEach((field) => {
      if (field.kind !== 'personalization') return;
      if (field.path === SET_ITSELF_PATH || !field.path.startsWith(prefix)) return;
      out.push({ id: field.path.slice(prefix.length), label: field.label });
    });
  });
  return out;
}

// ---------------------------------------------------------------------------
// What an input box means as a stored answer
// ---------------------------------------------------------------------------

/** Field types whose answer is stored as a number, as the form widget stores it. */
const NUMBER_FIELD_TYPES = new Set(['number', 'rating', 'nps', 'slider']);
/** Field types whose answer is stored as a boolean. */
const BOOLEAN_FIELD_TYPES = new Set(['boolean', 'consent']);
/** The one type that holds several answers at once. */
const MULTI_FIELD_TYPES = new Set(['checkbox']);

export function isMultiAnswerField(fieldType) {
  return MULTI_FIELD_TYPES.has(fieldType);
}
export function isBooleanAnswerField(fieldType) {
  return BOOLEAN_FIELD_TYPES.has(fieldType);
}
export function isNumberAnswerField(fieldType) {
  return NUMBER_FIELD_TYPES.has(fieldType);
}

/**
 * What a panel's input box means as a stored answer -- in the SAME JavaScript
 * type the form widget would store (src/forms-renderer/controls/): a number for
 * a number, a rating, an NPS score and a slider, a boolean for a yes/no, the
 * option value as a string for a choice, the typed text for everything else.
 *
 * The type is not decoration: a stored rule compares against the literal the
 * condition editor wrote (editor-v2/utils/conditionModel.ts keeps `10000` a
 * number precisely so it survives a round trip), and json-logic's `===` would
 * make "10000" and 10000 two different answers. A persona that stored the
 * string would quietly disagree with the reader on every numeric condition.
 *
 * An empty box is NO ANSWER (`undefined`), never an empty string: "has not
 * answered" and "answered with nothing" are different states, and only the
 * first one is what an untouched question is.
 *
 * Takes a STRING, the way a control hands it over. A value read back out of
 * storage goes through `storedAnswerValue` below instead, which knows about
 * lists and about values that are already of the right type.
 */
export function coercePreviewValue(fieldType, raw) {
  if (raw === '') return undefined;
  if (NUMBER_FIELD_TYPES.has(fieldType)) {
    const num = Number(raw);
    return Number.isFinite(num) ? num : undefined;
  }
  if (BOOLEAN_FIELD_TYPES.has(fieldType)) {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return undefined;
  }
  return raw;
}

/**
 * A multiple-choice answer after one box was ticked or unticked. An empty
 * selection is `undefined` -- the same "nothing left means no answer" the
 * checkbox control itself applies (`arr.length ? arr : undefined`).
 */
export function togglePreviewChoice(current, value, checked) {
  const list = Array.isArray(current) ? current.map(String) : [];
  let next;
  if (checked) {
    next = list.indexOf(value) === -1 ? [...list, value] : list;
  } else {
    next = list.filter((entry) => entry !== value);
  }
  return next.length ? next : undefined;
}

/** The imagined reader before anything has been said about them. */
export function emptyPreviewState() {
  return {
    answers: {}, personalizationSetId: null, personalizationValues: {}, urlQuery: '',
  };
}

// ---------------------------------------------------------------------------
// A persona as it is stored under theme_settings.answerPersonas
// ---------------------------------------------------------------------------

function plainObject(value) {
  return (value && typeof value === 'object' && !Array.isArray(value)) ? value : {};
}

/**
 * One stored answer, read back for a field of a known type.
 *
 * Not the same job as `coercePreviewValue`, which reads what a control just
 * handed over: this reads what is in the database, where the value is already
 * of its own type (a number stays a number), where a multiple-choice answer is
 * a list, and where a hand-edited entry can be anything at all. `undefined`
 * means "there is no usable answer here" and the caller drops the entry.
 */
function storedAnswerValue(fieldType, value) {
  if (value === undefined || value === null) return undefined;
  if (isMultiAnswerField(fieldType)) {
    const list = (Array.isArray(value) ? value : [value])
      .filter((one) => one !== null && one !== undefined && one !== '' && typeof one !== 'object')
      .map(String);
    return list.length ? list : undefined;
  }
  // A list where a single answer belongs is not something any control could
  // give back, and String([1,2]) would invent "1,2" out of it.
  if (Array.isArray(value) || typeof value === 'object') return undefined;
  if (isNumberAnswerField(fieldType)) {
    return Number.isFinite(Number(value)) ? Number(value) : undefined;
  }
  if (isBooleanAnswerField(fieldType)) {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return undefined;
  }
  return String(value);
}

/**
 * One persona in the shape everything here expects, out of whatever storage
 * holds. Never throws, never invents an id: a persona without one is dropped
 * by `normalizePersonas`, because the id is what a rename must not change and
 * what the editor's panel keys its buttons on.
 *
 * A persona without a NAME is not dropped -- it is labelled with its id. The
 * two are not the same case: without an id there is nothing to key a button on
 * and nothing a rename could keep stable, while a nameless persona is merely
 * unlabelled and its answers are perfectly good. Dropping it would delete a
 * hand-edited entry's work to avoid an ugly button. The id was made from a
 * name once (`idFromName`), so it is the closest thing to one that is left,
 * and the settings list shows it in the name box where it can be corrected.
 */
export function normalizePersona(raw) {
  const source = plainObject(raw);
  const answers = {};
  const storedAnswers = plainObject(source.answers);
  Object.keys(storedAnswers).forEach((formId) => {
    const entry = plainObject(storedAnswers[formId]);
    const lastPage = Number.isInteger(entry.lastPage) && entry.lastPage >= 0
      ? entry.lastPage : null;
    answers[formId] = { values: { ...plainObject(entry.values) }, lastPage };
  });
  const id = typeof source.id === 'string' ? source.id : '';
  const name = typeof source.name === 'string' ? source.name : '';
  return {
    id,
    name: name || id,
    answers,
    personalizationSetId: (source.personalizationSetId === null
      || source.personalizationSetId === undefined || source.personalizationSetId === '')
      ? null : String(source.personalizationSetId),
    personalizationValues: { ...plainObject(source.personalizationValues) },
    urlQuery: typeof source.urlQuery === 'string' ? source.urlQuery : '',
  };
}

/**
 * The stored list, healed. Entries without an id are dropped, and a repeated
 * id keeps its FIRST entry only.
 *
 * The second rule is not tidiness. The id is the identity of a persona: the
 * editor's panel keys its buttons on it and the settings look a persona up by
 * it, so two entries under one id are two rows Vue cannot tell apart and one
 * of them can never be edited or deleted. The panel that writes them refuses
 * a duplicate already (`addPersona`); this is for the hand-edited file that
 * got past it.
 */
export function normalizePersonas(list) {
  if (!Array.isArray(list)) return [];
  const seen = {};
  return list.map(normalizePersona).filter((persona) => {
    if (persona.id === '') return false;
    if (Object.prototype.hasOwnProperty.call(seen, persona.id)) return false;
    seen[persona.id] = true;
    return true;
  });
}

/**
 * A persona, turned into the state the preview runs on -- measured against the
 * field list AS IT IS TODAY.
 *
 * The measuring is the whole point. A persona names questions by their stored
 * path, and a redakteur can delete the question afterwards. An answer to a
 * question that no longer exists is not harmless: `buildData` would carry it,
 * a condition on the same path would still be decided against it, and the
 * preview would show a page nobody can ever see. So it is dropped -- and
 * COUNTED, so the panel can say it happened instead of quietly showing less
 * than the persona promised.
 *
 * The same measuring is applied to personalization: a set that is gone takes
 * its values with it, and a value whose variable is gone is dropped on its own.
 *
 * @returns {{ state: object, dropped: string[] }} `dropped` holds every stored
 *          path the preview could NOT take over, in the order they were read
 *          -- a field that is gone, and a stored value no control could have
 *          produced for the field it points at (a hand-edited entry). One list,
 *          because the panel says the same thing about both: this angabe did
 *          not make it into the preview.
 */
export function previewStateFromPersona(persona, groups) {
  const clean = normalizePersona(persona);
  const state = emptyPreviewState();
  const dropped = [];

  const answerFields = {};
  previewAnswerGroups(groups).forEach((group) => {
    group.fields.forEach((field) => { answerFields[field.path] = field; });
  });

  const formIsStillThere = (formId) => Object.keys(answerFields).some((path) => {
    const parts = splitAnswerPath(path);
    return parts !== null && parts.formId === formId;
  });

  Object.keys(clean.answers).forEach((formId) => {
    const entry = clean.answers[formId];
    const values = {};
    Object.keys(entry.values).forEach((fieldId) => {
      const path = `form.${formId}.${fieldId}`;
      const field = answerFields[path];
      if (!field) { dropped.push(path); return; }
      const value = storedAnswerValue(field.fieldType, entry.values[fieldId]);
      if (value === undefined) { dropped.push(path); return; }
      values[fieldId] = value;
    });
    // The whole entry goes when the FORM is gone: `lastPage` alone would put a
    // form into the shape `wbmgAnswers.all()` hands the evaluator, for a form
    // that no page can show any more.
    if (!formIsStillThere(formId)) return;
    state.answers[formId] = { values, otherValues: {}, lastPage: entry.lastPage };
  });

  const setIds = previewPersonalizationSets(groups).map((set) => set.id);
  if (clean.personalizationSetId !== null) {
    if (setIds.indexOf(clean.personalizationSetId) === -1) {
      dropped.push(`${SET_ITSELF_PATH}=${clean.personalizationSetId}`);
    } else {
      state.personalizationSetId = clean.personalizationSetId;
      const known = previewPersonalizationFields(groups, clean.personalizationSetId)
        .map((entry) => entry.id);
      Object.keys(clean.personalizationValues).forEach((fieldId) => {
        const path = `${clean.personalizationSetId}.${fieldId}`;
        if (known.indexOf(fieldId) === -1) { dropped.push(path); return; }
        state.personalizationValues[fieldId] = String(clean.personalizationValues[fieldId]);
      });
    }
  }

  state.urlQuery = clean.urlQuery;
  return { state, dropped };
}

/**
 * How many single things an imagined reader is made of right now.
 *
 * The other side of `personaFillCount`, and the same counting, which is why
 * that one is written in terms of this. Applying a persona REPLACES the whole
 * reader, so this is what a click is about to throw away: the panel warns
 * with it before the click and names it afterwards.
 */
export function previewStateFillCount(state) {
  if (!state) return 0;
  let count = 0;
  Object.keys(state.answers || {}).forEach((formId) => {
    const entry = state.answers[formId];
    if (!entry) return;
    count += Object.keys(entry.values || {}).length;
    if (entry.lastPage !== null && entry.lastPage !== undefined) count += 1;
  });
  if (state.personalizationSetId !== null && state.personalizationSetId !== undefined) count += 1;
  count += Object.keys(state.personalizationValues || {}).length;
  if (state.urlQuery) count += 1;
  return count;
}

/**
 * How many single things a persona would really put into the preview HERE --
 * every answer it can still fill, every remembered form page, the
 * personalization set and each of its values, and the address bar.
 *
 * WHY THIS EXISTS. The two panels do not see the same questions. The settings
 * build their field list from the PROJECT's item path
 * (ProjectSettings.vue's segmentFormsData), the editor from the PAGE's
 * (PageItem.vue's formsData), and the project's list is larger by
 * construction. So in any project with more than one form there are personas
 * that can fill nothing at all on a given page -- not an edge case, the normal
 * case. Applying one would wipe what the editor had typed by hand and put
 * nothing in its place.
 *
 * Zero therefore has to be knowable BEFORE the click, which is what this is
 * for. It counts the applied state and not the stored persona, so it agrees
 * with what would really happen down to the single answer.
 */
export function personaFillCount(persona, groups) {
  return previewStateFillCount(previewStateFromPersona(persona, groups).state);
}

// ---------------------------------------------------------------------------
// Reading a persona back in words
// ---------------------------------------------------------------------------

const EMPTY_SUMMARY = 'Diese Testperson hat noch nichts beantwortet.';

function labelForValue(field, value) {
  const values = Array.isArray(value) ? value : [value];
  const names = values.map((one) => {
    if (one === true) return 'Ja';
    if (one === false) return 'Nein';
    const key = String(one);
    const option = (field.options || []).find((entry) => entry.value === key);
    return option ? option.label : key;
  });
  return names.join(', ');
}

/**
 * What a persona stands for, as one German line -- built from the SAME reading
 * that fills the preview (`previewStateFromPersona`), so the list can never
 * promise an answer the preview would drop.
 *
 * German, hardcoded, like every other word the condition sentence next door is
 * built from (conditionSentence.js): this product has no second interface
 * language for these panels.
 */
export function personaSummary(persona, groups) {
  const { state, dropped } = previewStateFromPersona(persona, groups);
  const answerFields = {};
  previewAnswerGroups(groups).forEach((group) => {
    group.fields.forEach((field) => { answerFields[field.path] = field; });
  });

  const parts = [];
  Object.keys(state.answers).forEach((formId) => {
    const entry = state.answers[formId];
    Object.keys(entry.values).forEach((fieldId) => {
      const field = answerFields[`form.${formId}.${fieldId}`];
      if (!field) return;
      parts.push(`${field.label}: ${labelForValue(field, entry.values[fieldId])}`);
    });
  });

  if (state.personalizationSetId !== null) {
    const set = previewPersonalizationSets(groups)
      .find((entry) => entry.id === state.personalizationSetId);
    if (set) parts.push(`Personalisierungs-Set: ${set.label}`);
  }
  if (state.urlQuery) parts.push(`Adresszeile: ${state.urlQuery}`);

  const sentence = parts.length ? `${parts.join(' · ')}.` : EMPTY_SUMMARY;
  if (dropped.length === 0) return sentence;
  const note = dropped.length === 1
    ? 'Eine Angabe lässt sich nicht mehr übernehmen: das Feld gibt es nicht mehr, '
      + 'oder der gespeicherte Wert passt nicht dazu.'
    : `${dropped.length} Angaben lassen sich nicht mehr übernehmen: die Felder gibt es `
      + 'nicht mehr, oder die gespeicherten Werte passen nicht dazu.';
  return `${sentence} ${note}`;
}
